import React, { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import SSMusicPlayer from './SSMusicPlayer';
import './VideoPlayer.css';

const disableCaptions = (player) => {
  // Saved YouTube preferences can override cc_load_policy. Only unload a loaded
  // module because unloading also triggers onApiChange.
  if (player.getOptions?.()?.includes('captions')) {
    player.unloadModule?.('captions');
  }
};

const VideoPlayer = forwardRef(function VideoPlayer({ videoId, playbackKey, mediaPath, mediaType, title, paused = false, processing = false, processingError, onEnded, onRestricted, settings, onPrev, hasPrev, onNext, hasNext, onTimeUpdate }, ref) {
  const playerRef = useRef(null);
  const nativePlayerRef = useRef(null);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const processingRef = useRef(processing);
  processingRef.current = processing;
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const mouseTimerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const onEndedRef = useRef(onEnded);
  const onRestrictedRef = useRef(onRestricted);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  useEffect(() => { onEndedRef.current = onEnded; }, [onEnded]);
  useEffect(() => { onRestrictedRef.current = onRestricted; }, [onRestricted]);
  useEffect(() => { onTimeUpdateRef.current = onTimeUpdate; }, [onTimeUpdate]);

  // Expose seekTo/pauseVideo/playVideo so parent pages can programmatically control the player
  useImperativeHandle(ref, () => ({
    seekTo: (seconds) => {
      if (nativePlayerRef.current) return nativePlayerRef.current.seekTo(seconds);
      if (playerRef.current?.seekTo) {
        playerRef.current.seekTo(seconds, true);
      }
    },
    pauseVideo: () => {
      if (nativePlayerRef.current) return nativePlayerRef.current.pauseVideo();
      playerRef.current?.pauseVideo?.();
    },
    playVideo: () => {
      if (processingRef.current) return;
      if (nativePlayerRef.current) return nativePlayerRef.current.playVideo();
      playerRef.current?.playVideo?.();
    },
  }), []);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);

  useEffect(() => {
    if (mediaPath || !videoId) return;
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode.insertBefore(tag, firstScript);
    }
  }, [mediaPath, videoId]);

  useEffect(() => {
    if (!videoId || mediaPath) return;
    let disposed = false;
    let restrictionReported = false;

    const initPlayer = () => {
      if (disposed || !containerRef.current) return;
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      // Create a nested element for YouTube to replace so that containerRef.current
      // (React's tracked node) is never removed from the DOM by the YouTube API.
      // This prevents React's removeChild from failing when reconciling after a
      // videoId change (e.g. pausing a song).
      const playerTarget = document.createElement('div');
      containerRef.current.appendChild(playerTarget);
      playerRef.current = new window.YT.Player(playerTarget, {
        videoId,
        playerVars: {
          autoplay: 1,
          controls: 1,
          rel: 0,
          modestbranding: 1,
          fs: 0,
          cc_load_policy: 0,
        },
        events: {
          onReady: (event) => {
            disableCaptions(event.target);
            if (pausedRef.current || processingRef.current) event.target.pauseVideo();
            else event.target.playVideo();
            applySettings(event.target);
          },
          onApiChange: (event) => {
            disableCaptions(event.target);
          },
          onError: (event) => {
            if (disposed || restrictionReported || ![100, 101, 150].includes(event.data)) return;
            restrictionReported = true;
            clearInterval(progressIntervalRef.current);
            onRestrictedRef.current?.({ videoId, playbackKey });
          },
          onStateChange: (event) => {
            if (disposed || restrictionReported) return;
            if (event.data === window.YT.PlayerState.PLAYING) {
              disableCaptions(event.target);
              // Poll playback position every 2 seconds while playing
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = setInterval(() => {
                if (playerRef.current?.getCurrentTime) {
                  const ct = playerRef.current.getCurrentTime();
                  const dur = playerRef.current.getDuration();
                  onTimeUpdateRef.current?.(ct, dur);
                }
              }, 2000);
            } else {
              clearInterval(progressIntervalRef.current);
            }
            if (event.data === window.YT.PlayerState.ENDED && onEndedRef.current) {
              onEndedRef.current();
            }
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      window.onYouTubeIframeAPIReady = initPlayer;
    }

    // Capture ref here so the cleanup closure sees the same node
    const container = containerRef.current;
    return () => {
      disposed = true;
      if (window.onYouTubeIframeAPIReady === initPlayer) window.onYouTubeIframeAPIReady = null;
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      // Remove the child element YouTube replaced so it doesn't accumulate
      // across video changes.
      if (container) {
        container.innerHTML = '';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-creating the player on each render would be disruptive; onEnded is kept current via onEndedRef
  }, [videoId, mediaPath, playbackKey]);

  useEffect(() => {
    if (paused || processing) playerRef.current?.pauseVideo?.();
  }, [paused, processing]);

  const applySettings = (player) => {
    if (!player || !settings) return;
    // Volume proxy for vocal level (0–100)
    if (settings.vocalLevel !== undefined) {
      player.setVolume(settings.vocalLevel);
    }
    // Playback rate proxy for tempo (0.5 – 2.0)
    if (settings.tempo !== undefined) {
      try {
        player.setPlaybackRate(settings.tempo);
      } catch (err) {
        console.warn('setPlaybackRate not supported:', err);
      }
    }
  };

  // Re-apply settings whenever they change
  useEffect(() => {
    if (playerRef.current && playerRef.current.setVolume) {
      applySettings(playerRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- applySettings is defined in component scope and stable; settings is the only changing dep
  }, [settings]);

  // Fullscreen change detection
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fs = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(fs);
      clearTimeout(mouseTimerRef.current);
      if (fs) {
        setControlsVisible(true);
        mouseTimerRef.current = setTimeout(() => setControlsVisible(false), 2000);
      } else {
        setControlsVisible(true);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      clearTimeout(mouseTimerRef.current);
    };
  }, []);

  const handleMouseMove = useCallback(() => {
    if (!isFullscreen) return;
    setControlsVisible(true);
    clearTimeout(mouseTimerRef.current);
    mouseTimerRef.current = setTimeout(() => setControlsVisible(false), 2000);
  }, [isFullscreen]);

  const toggleFullscreen = useCallback(() => {
    const el = wrapperRef.current;
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (el.requestFullscreen) {
        el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  }, []);

  if (!videoId) {
    return (
      <div className="video-placeholder">
        <div className="video-placeholder-inner">
          <span className="video-placeholder-icon">🎵</span>
          <p>No song playing</p>
          <p className="muted">Add songs to the queue to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className={`video-fullscreen-wrapper${isFullscreen && !controlsVisible ? ' controls-hidden' : ''}`}
      onMouseMove={handleMouseMove}
    >
      <div className="video-container">
        {mediaPath ? (
          <SSMusicPlayer
            key={playbackKey || mediaPath}
            ref={nativePlayerRef}
            mediaPath={mediaPath}
            mediaType={mediaType}
            title={title}
            paused={paused}
            settings={settings}
            onEnded={onEnded}
            onTimeUpdate={onTimeUpdate}
          />
        ) : <div ref={containerRef} id="yt-player" hidden={processing || Boolean(processingError)} />}
        {!mediaPath && (processing || processingError) && (
          <div className="video-processing-message" role={processing ? 'status' : 'alert'}>
            {processing ? 'Video is processing, come back when done' : processingError}
          </div>
        )}
      </div>

      {/* Fullscreen overlay nav buttons */}
      {isFullscreen && (
        <>
          {hasPrev && (
            <button
              className={`fullscreen-nav-btn fullscreen-prev-btn${controlsVisible ? ' visible' : ''}`}
              onClick={onPrev}
              aria-label="Previous song"
            >
              &#9664;
            </button>
          )}
          {hasNext && (
            <button
              className={`fullscreen-nav-btn fullscreen-next-btn${controlsVisible ? ' visible' : ''}`}
              onClick={onNext}
              aria-label="Next song"
            >
              &#9654;
            </button>
          )}
        </>
      )}

      {/* Below-video nav controls (hidden in fullscreen) */}
      {!isFullscreen && (
        <div className="video-nav-controls">
          <button
            className="nav-btn nav-btn-fullscreen"
            onClick={toggleFullscreen}
            aria-label="Enter fullscreen"
          >
            ⛶ Fullscreen
          </button>
          {hasPrev && (
            <button
              className="nav-btn"
              onClick={onPrev}
              aria-label="Previous song"
            >
              ⏮ Previous
            </button>
          )}
          {hasNext && (
            <button
              className="nav-btn"
              onClick={onNext}
              aria-label="Next song"
            >
              Next ⏭
            </button>
          )}
        </div>
      )}
    </div>
  );
});

export default VideoPlayer;
